/**
 * G2eGente - Job Candidates Management (Unified View)
 * All candidate management happens within the job context
 * - Search and add candidates
 * - View solicitation status
 * - Chat with candidates
 * - Interview evaluation
 * - Export PDF
 */
import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { DashboardLayout, companyMenuItems } from '../../components/DashboardLayout';
import { jobsAPI, solicitationsAPI, candidatesAPI, evaluationsAPI, chatAPI } from '../../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Separator } from '../../components/ui/separator';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { Textarea } from '../../components/ui/textarea';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { ScheduleInterviewButton } from '../../components/interviews/ScheduleInterviewDialog';
import { JobInterviewsSection } from '../../components/interviews/JobInterviewsSection';
import { MatchBadge, useMatches } from '../../components/match/MatchBadge';
import { matchesAPI } from '../../lib/api';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from '../../components/ui/sheet';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../../components/ui/alert-dialog';
import { 
  ArrowLeft,
  Users,
  UserPlus,
  Loader2,
  MapPin,
  Brain,
  Star,
  CheckCircle,
  XCircle,
  Clock,
  Pin,
  PinOff,
  Eye,
  ClipboardList,
  FileText,
  Download,
  Filter,
  User,
  Phone,
  Mail,
  Briefcase,
  Award,
  GraduationCap,
  X,
  AlertCircle,
  Save,
  Target,
  Heart,
  Lightbulb,
  Crown,
  Search,
  MessageSquare,
  Send,
  Lock,
  Plus,
  BarChart3,
  RefreshCw
} from 'lucide-react';
import { toast } from 'sonner';

const JOB_STATUS_OPTIONS = [
  { value: 'em_andamento', label: 'Em Andamento', color: 'bg-blue-100 text-blue-700' },
  { value: 'em_selecao', label: 'Em Seleção', color: 'bg-amber-100 text-amber-700' },
  { value: 'finalizada', label: 'Finalizada', color: 'bg-green-100 text-green-700' },
  { value: 'cancelada', label: 'Cancelada', color: 'bg-red-100 text-red-700' }
];

// Star rating component
const StarRating = ({ value, onChange, disabled }) => {
  return (
    <div className="flex gap-1">
      {[1, 2, 3, 4, 5].map((star) => (
        <button
          key={star}
          type="button"
          disabled={disabled}
          onClick={() => onChange && onChange(star)}
          className={`transition-colors ${disabled ? 'cursor-default' : 'cursor-pointer hover:scale-110'}`}
        >
          <Star 
            className={`w-5 h-5 ${
              star <= value 
                ? 'fill-amber-400 text-amber-400' 
                : 'text-slate-300'
            }`}
          />
        </button>
      ))}
    </div>
  );
};

// Mini Radar Chart for Big Five (compact version for sidebar)
const BigFiveMiniRadar = ({ scores }) => {
  const traits = ['O', 'C', 'E', 'A', 'N'];
  const colors = ['#a855f7', '#3b82f6', '#f59e0b', '#f43f5e', '#10b981'];
  
  const centerX = 60;
  const centerY = 60;
  const radius = 45;
  
  const normalizedScores = traits.map(t => (scores[t] || 50) / 100);
  
  const points = normalizedScores.map((score, i) => {
    const angle = (Math.PI * 2 * i) / traits.length - Math.PI / 2;
    const x = centerX + Math.cos(angle) * radius * score;
    const y = centerY + Math.sin(angle) * radius * score;
    return `${x},${y}`;
  }).join(' ');
  
  return (
    <svg viewBox="0 0 120 120" className="w-32 h-32">
      {/* Grid */}
      {[0.5, 1].map((level, i) => (
        <polygon
          key={i}
          points={traits.map((_, j) => {
            const angle = (Math.PI * 2 * j) / traits.length - Math.PI / 2;
            const x = centerX + Math.cos(angle) * radius * level;
            const y = centerY + Math.sin(angle) * radius * level;
            return `${x},${y}`;
          }).join(' ')}
          fill="none"
          stroke="#e2e8f0"
          strokeWidth="1"
        />
      ))}
      
      {/* Axes */}
      {traits.map((_, i) => {
        const angle = (Math.PI * 2 * i) / traits.length - Math.PI / 2;
        const x = centerX + Math.cos(angle) * radius;
        const y = centerY + Math.sin(angle) * radius;
        return (
          <line
            key={i}
            x1={centerX}
            y1={centerY}
            x2={x}
            y2={y}
            stroke="#cbd5e1"
            strokeWidth="0.5"
          />
        );
      })}
      
      {/* Filled area */}
      <polygon
        points={points}
        fill="rgba(139, 92, 246, 0.25)"
        stroke="#8b5cf6"
        strokeWidth="1.5"
      />
      
      {/* Points */}
      {normalizedScores.map((score, i) => {
        const angle = (Math.PI * 2 * i) / traits.length - Math.PI / 2;
        const x = centerX + Math.cos(angle) * radius * score;
        const y = centerY + Math.sin(angle) * radius * score;
        return (
          <circle
            key={i}
            cx={x}
            cy={y}
            r="3"
            fill={colors[i]}
            stroke="white"
            strokeWidth="1"
          />
        );
      })}
      
      {/* Labels */}
      {traits.map((trait, i) => {
        const angle = (Math.PI * 2 * i) / traits.length - Math.PI / 2;
        const labelRadius = radius + 12;
        const x = centerX + Math.cos(angle) * labelRadius;
        const y = centerY + Math.sin(angle) * labelRadius;
        return (
          <text
            key={i}
            x={x}
            y={y + 3}
            textAnchor="middle"
            className="text-[8px] font-semibold"
            fill={colors[i]}
          >
            {trait}
          </text>
        );
      })}
    </svg>
  );
};

export default function CompanyJobCandidates() {
  const { jobId } = useParams();
  const navigate = useNavigate();
  
  const [job, setJob] = useState(null);
  const [candidates, setCandidates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedCandidate, setSelectedCandidate] = useState(null);
  const [candidateDetail, setCandidateDetail] = useState(null);
  const [loadingDetail, setLoadingDetail] = useState(false);
  const [showOnlySelected, setShowOnlySelected] = useState(false);
  const [statusFilter, setStatusFilter] = useState('');
  const [updatingStatus, setUpdatingStatus] = useState(false);
  const [showStatusDialog, setShowStatusDialog] = useState(false);
  const [newJobStatus, setNewJobStatus] = useState('');
  const [reopening, setReopening] = useState(false);
  
  // Ranking state
  const [ranking, setRanking] = useState([]);
  const [loadingRanking, setLoadingRanking] = useState(false);
  const [showRanking, setShowRanking] = useState(false);
  
  // Search modal state
  const [showSearchModal, setShowSearchModal] = useState(false);
  const [searchResults, setSearchResults] = useState([]);
  const [searchLoading, setSearchLoading] = useState(false);
  const [searchFilters, setSearchFilters] = useState({
    cargos: [], // Multiple cargos support
    localizacao: '',
    competencias: '',
    disc: '',
    pcd: '',
    estagiario: '',
    aprendiz: ''
  });
  const [newCargoInput, setNewCargoInput] = useState('');
  const [inviting, setInviting] = useState(null);
  
  // Chat state
  const [showChatSheet, setShowChatSheet] = useState(false);
  const [chatMessages, setChatMessages] = useState([]);
  const [chatLoading, setChatLoading] = useState(false);
  const [newMessage, setNewMessage] = useState('');
  const [sendingMessage, setSendingMessage] = useState(false);
  const [chatId, setChatId] = useState(null);
  
  // Evaluation state
  const [showEvaluation, setShowEvaluation] = useState(false);
  const [loadingEvaluation, setLoadingEvaluation] = useState(false);
  const [evaluationTemplate, setEvaluationTemplate] = useState(null);
  const [evaluation, setEvaluation] = useState(null);
  const [savingEvaluation, setSavingEvaluation] = useState(false);
  const [newPontoForte, setNewPontoForte] = useState('');
  const [newPontoMelhoria, setNewPontoMelhoria] = useState('');
  
  // Big Five request state
  const [requestingBigFive, setRequestingBigFive] = useState(null);
  const [interviewsKey, setInterviewsKey] = useState(0);
  const jobMatches = useMatches(() => matchesAPI.company(candidates.slice(0, 40).map((c) => ({ candidate_id: c.candidate_id, job_id: jobId }))),
    candidates.length ? `${jobId}|${candidates.map((c) => c.candidate_id).join(',')}` : null);
  useEffect(() => {
    fetchJobData();
    fetchEvaluationTemplate();
  }, [jobId]);

  useEffect(() => {
    if (job) {
      fetchCandidates();
    }
  }, [job, showOnlySelected, statusFilter]);

  const fetchJobData = async () => {
    try {
      const response = await jobsAPI.getById(jobId);
      setJob(response.data);
    } catch (error) {
      toast.error('Erro ao carregar vaga');
      navigate('/company/jobs');
    } finally {
      setLoading(false);
    }
  };

  const fetchEvaluationTemplate = async () => {
    try {
      const response = await evaluationsAPI.getTemplate();
      setEvaluationTemplate(response.data);
    } catch (error) {
      console.error('Error fetching evaluation template:', error);
    }
  };

  const fetchCandidates = async () => {
    try {
      const params = {};
      if (showOnlySelected) params.only_selected = true;
      if (statusFilter && statusFilter !== 'all') params.status = statusFilter;
      
      const response = await jobsAPI.getCandidates(jobId, params);
      setCandidates(response.data);
    } catch (error) {
      toast.error('Erro ao carregar candidatos');
    }
  };

  const fetchRanking = async () => {
    setLoadingRanking(true);
    try {
      const response = await jobsAPI.getRanking(jobId);
      setRanking(response.data.ranking || []);
      if (!response.data.ranking?.length && response.data.message) {
        toast.info(response.data.message);
      }
    } catch (error) {
      toast.error('Erro ao carregar ranking');
    } finally {
      setLoadingRanking(false);
    }
  };

  const handleReopenJob = async () => {
    setReopening(true);
    try {
      const response = await jobsAPI.reopen(jobId);
      setJob(response.data);
      toast.success('Vaga reaberta com sucesso!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao reabrir vaga');
    } finally {
      setReopening(false);
    }
  };

  // Search functions
  const handleSearch = async () => {
    setSearchLoading(true);
    try {
      const params = {};
      // Multiple cargos - join with comma for backend
      if (searchFilters.cargos.length > 0) params.cargos = searchFilters.cargos.join(',');
      if (searchFilters.localizacao) params.localizacao = searchFilters.localizacao;
      if (searchFilters.competencias) params.competencias = searchFilters.competencias;
      if (searchFilters.disc && searchFilters.disc !== 'all') params.disc_profile = searchFilters.disc;
      if (searchFilters.pcd && searchFilters.pcd !== 'all') params.pcd = searchFilters.pcd;
      if (searchFilters.estagiario && searchFilters.estagiario !== 'all') params.estagiario = searchFilters.estagiario;
      if (searchFilters.aprendiz && searchFilters.aprendiz !== 'all') params.aprendiz = searchFilters.aprendiz;
      
      const response = await candidatesAPI.search(params);
      
      // Filter out candidates already in this job
      const existingIds = candidates.map(c => c.candidate_id);
      const filtered = response.data.filter(c => !existingIds.includes(c.id));
      
      setSearchResults(filtered);
      
      if (filtered.length === 0) {
        toast.info('Nenhum candidato novo encontrado');
      } else {
        toast.success(`${filtered.length} candidato(s) encontrado(s)`);
      }
    } catch (error) {
      toast.error('Erro ao buscar candidatos');
    } finally {
      setSearchLoading(false);
    }
  };
  
  const handleAddCargo = () => {
    const cargo = newCargoInput.trim();
    if (cargo && !searchFilters.cargos.includes(cargo)) {
      setSearchFilters(prev => ({
        ...prev,
        cargos: [...prev.cargos, cargo]
      }));
      setNewCargoInput('');
    }
  };
  
  const handleRemoveCargo = (cargoToRemove) => {
    setSearchFilters(prev => ({
      ...prev,
      cargos: prev.cargos.filter(c => c !== cargoToRemove)
    }));
  };

  const handleInviteCandidate = async (candidateId) => {
    setInviting(candidateId);
    try {
      await solicitationsAPI.create({ candidate_id: candidateId, job_id: jobId });
      toast.success('Convite enviado ao candidato!');
      // Remove from search results
      setSearchResults(prev => prev.filter(c => c.id !== candidateId));
      // Refresh candidates list
      fetchCandidates();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao enviar convite');
    } finally {
      setInviting(null);
    }
  };

  const handleSelectCandidate = async (candidateId) => {
    try {
      await jobsAPI.selectCandidate(jobId, candidateId);
      toast.success('Candidato fixado na vaga!');
      fetchCandidates();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao fixar candidato');
    }
  };

  const handleUnselectCandidate = async (candidateId) => {
    try {
      await jobsAPI.unselectCandidate(jobId, candidateId);
      toast.success('Candidato removido da seleção');
      fetchCandidates();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao remover candidato');
    }
  };

  const handleUpdateJobStatus = async () => {
    if (!newJobStatus) return;
    setUpdatingStatus(true);
    try {
      await jobsAPI.updateStatus(jobId, newJobStatus);
      setJob({ ...job, status: newJobStatus });
      toast.success('Status da vaga atualizado!');
      setShowStatusDialog(false);
    } catch (error) {
      toast.error('Erro ao atualizar status');
    } finally {
      setUpdatingStatus(false);
    }
  };

  const handleViewCandidate = async (candidate) => {
    setSelectedCandidate(candidate);
    setLoadingDetail(true);
    
    try {
      const response = await candidatesAPI.getById(candidate.candidate_id);
      setCandidateDetail(response.data);
    } catch (error) {
      setCandidateDetail(null);
    }
    setLoadingDetail(false);
  };

  // Chat functions
  const openChat = async (candidate) => {
    if (candidate.status !== 'APPROVED') {
      toast.info('O chat abre quando o candidato aceita o convite. Enquanto isso, use o telefone ou WhatsApp do perfil.');
      return;
    }
    
    setSelectedCandidate(candidate);
    setShowChatSheet(true);
    setChatLoading(true);
    
    try {
      // Get or create chat
      const chatsResponse = await chatAPI.getAll();
      const existingChat = chatsResponse.data.find(
        c => c.candidate_id === candidate.candidate_id
      );
      
      if (existingChat) {
        setChatId(existingChat.id);
        const messagesResponse = await chatAPI.getMessages(existingChat.id);
        setChatMessages(messagesResponse.data.messages || []);
      } else {
        setChatId(null);
        setChatMessages([]);
      }
    } catch (error) {
      toast.error('Erro ao carregar chat');
    } finally {
      setChatLoading(false);
    }
  };

  const sendMessage = async () => {
    if (!newMessage.trim() || !selectedCandidate) return;
    
    setSendingMessage(true);
    try {
      let currentChatId = chatId;
      
      if (!currentChatId) {
        // Create new chat
        const createResponse = await chatAPI.create(selectedCandidate.candidate_id);
        currentChatId = createResponse.data.id;
        setChatId(currentChatId);
      }
      
      // Send message
      const response = await chatAPI.sendMessage(currentChatId, newMessage);
      setChatMessages(response.data.messages || []);
      setNewMessage('');
    } catch (error) {
      toast.error('Erro ao enviar mensagem');
    } finally {
      setSendingMessage(false);
    }
  };

  // Evaluation functions
  const initializeEvaluation = async (candidateToEvaluate = null) => {
    const candidate = candidateToEvaluate || selectedCandidate;
    if (!evaluationTemplate || !candidate) {
      console.log('Cannot initialize evaluation: missing template or candidate');
      return;
    }
    
    // Set loading state first to prevent Sheet from opening
    setLoadingEvaluation(true);
    
    // Set the candidate if passed as parameter
    if (candidateToEvaluate) {
      setSelectedCandidate(candidateToEvaluate);
    }
    
    // Try to load existing evaluation for this candidate in this job
    try {
      const response = await evaluationsAPI.getAll({ 
        candidate_id: candidate.candidate_id, 
        job_id: jobId 
      });
      
      if (response.data && response.data.length > 0) {
        // Load existing evaluation
        const existingEval = response.data[0];
        setEvaluation({
          id: existingEval.id,
          niveis: existingEval.niveis || [],
          resumo_geral: existingEval.resumo_geral || '',
          recomendacao: existingEval.recomendacao || '',
          pontos_fortes: existingEval.pontos_fortes || [],
          pontos_melhoria: existingEval.pontos_melhoria || [],
          status: existingEval.status
        });
        setShowEvaluation(true);
        setLoadingEvaluation(false);
        return;
      }
    } catch (error) {
      console.log('No existing evaluation found, creating new one');
    }
    
    // Create new evaluation from template
    // Template comes as {niveis: [{nivel: 'entrega', nome: '...', steps: [...]}]}
    const templateNiveis = evaluationTemplate.niveis || evaluationTemplate;
    const niveis = (Array.isArray(templateNiveis) ? templateNiveis : Object.entries(templateNiveis).map(([key, nivel]) => ({...nivel, nivel: key}))).map(nivel => ({
      nivel: nivel.nivel,
      nome: nivel.nome,
      descricao: nivel.descricao,
      steps: nivel.steps.map(step => ({
        nome: step.nome,
        descricao: step.descricao,
        pontuacao: 0,
        observacao: ''
      })),
      comentario: ''
    }));
    
    setEvaluation({
      niveis,
      resumo_geral: '',
      recomendacao: '',
      pontos_fortes: [],
      pontos_melhoria: []
    });
    setShowEvaluation(true);
    setLoadingEvaluation(false);
  };

  const updateStepScore = (nivelIndex, stepIndex, pontuacao) => {
    setEvaluation(prev => {
      const newNiveis = [...prev.niveis];
      newNiveis[nivelIndex].steps[stepIndex].pontuacao = pontuacao;
      return { ...prev, niveis: newNiveis };
    });
  };

  const updateStepObservacao = (nivelIndex, stepIndex, observacao) => {
    setEvaluation(prev => {
      const newNiveis = [...prev.niveis];
      newNiveis[nivelIndex].steps[stepIndex].observacao = observacao;
      return { ...prev, niveis: newNiveis };
    });
  };

  const updateNivelComentario = (nivelIndex, comentario) => {
    setEvaluation(prev => {
      const newNiveis = [...prev.niveis];
      newNiveis[nivelIndex].comentario = comentario;
      return { ...prev, niveis: newNiveis };
    });
  };

  const addPontoForte = () => {
    if (newPontoForte.trim()) {
      setEvaluation(prev => ({
        ...prev,
        pontos_fortes: [...prev.pontos_fortes, newPontoForte.trim()]
      }));
      setNewPontoForte('');
    }
  };

  const addPontoMelhoria = () => {
    if (newPontoMelhoria.trim()) {
      setEvaluation(prev => ({
        ...prev,
        pontos_melhoria: [...prev.pontos_melhoria, newPontoMelhoria.trim()]
      }));
      setNewPontoMelhoria('');
    }
  };

  const removePonto = (type, index) => {
    setEvaluation(prev => ({
      ...prev,
      [type]: prev[type].filter((_, i) => i !== index)
    }));
  };

  const calculateNivelMedia = (nivel) => {
    const scores = nivel.steps.filter(s => s.pontuacao > 0).map(s => s.pontuacao);
    return scores.length > 0 ? (scores.reduce((a, b) => a + b, 0) / scores.length).toFixed(1) : '-';
  };

  const calculateOverallMedia = () => {
    if (!evaluation) return '-';
    const allScores = evaluation.niveis.flatMap(n => n.steps.filter(s => s.pontuacao > 0).map(s => s.pontuacao));
    return allScores.length > 0 ? (allScores.reduce((a, b) => a + b, 0) / allScores.length).toFixed(1) : '-';
  };

  const handleSaveEvaluation = async (status = 'rascunho') => {
    if (!selectedCandidate || !evaluation) return;
    
    setSavingEvaluation(true);
    try {
      const evaluationData = {
        candidate_id: selectedCandidate.candidate_id,
        job_id: jobId,
        niveis: evaluation.niveis,
        resumo_geral: evaluation.resumo_geral,
        recomendacao: evaluation.recomendacao,
        pontos_fortes: evaluation.pontos_fortes,
        pontos_melhoria: evaluation.pontos_melhoria,
        status
      };
      
      if (evaluation.id) {
        // Update existing evaluation
        await evaluationsAPI.update(evaluation.id, evaluationData);
      } else {
        // Create new evaluation
        await evaluationsAPI.create(evaluationData);
      }
      
      toast.success(status === 'finalizado' ? 'Avaliação finalizada!' : 'Rascunho salvo!');
      setShowEvaluation(false);
      setSelectedCandidate(null); // Reset to prevent detail sheet from opening
      setEvaluation(null);
      fetchCandidates();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao salvar avaliação');
    } finally {
      setSavingEvaluation(false);
    }
  };

  // PDF Export
  // Handle requesting new Big Five assessment
  const handleRequestNewBigFive = async (candidate) => {
    if (!candidate || !job) return;
    
    setRequestingBigFive(candidate.id);
    try {
      const API_URL = process.env.REACT_APP_BACKEND_URL;
      const token = localStorage.getItem('token');
      
      // Get company info
      const companyRes = await fetch(`${API_URL}/api/companies/me`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const company = await companyRes.json();
      
      // Create notification for candidate
      const response = await fetch(`${API_URL}/api/notifications`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          user_id: candidate.user_id,
          type: 'BIGFIVE_REQUIRED',
          title: 'Nova Avaliação Big Five Solicitada',
          message: `A empresa ${company.nome || 'Empresa'} solicitou uma nova avaliação Big Five para a vaga "${job.titulo}".`,
          data: {
            job_id: job.id,
            job_title: job.titulo,
            bigfive_version: job.bigfive_required || 'BFI-44',
            company_name: company.nome || 'Empresa'
          }
        })
      });
      
      if (response.ok) {
        toast.success('Solicitação enviada ao candidato!');
      } else {
        throw new Error('Erro ao enviar solicitação');
      }
    } catch (error) {
      console.error('Error requesting Big Five:', error);
      toast.error('Erro ao solicitar nova avaliação');
    } finally {
      setRequestingBigFive(null);
    }
  };

  const handleExportPDF = () => {
    toast.info('Gerando PDF... (funcionalidade em desenvolvimento)');
    // TODO: Implement PDF generation with jspdf
  };

  const getNivelIcon = (nivel) => {
    const icons = {
      entrega: Target,
      comportamento: Heart,
      cognitivo: Brain,
      aspiracao: Lightbulb,
      experiencia: Briefcase,
      lideranca: Crown
    };
    return icons[nivel] || Star;
  };

  const getStatusColor = (status) => {
    const option = JOB_STATUS_OPTIONS.find(o => o.value === status);
    return option?.color || 'bg-slate-100 text-slate-700';
  };

  const getStatusLabel = (status) => {
    const option = JOB_STATUS_OPTIONS.find(o => o.value === status);
    return option?.label || status;
  };

  const getCandidateStatusBadge = (status, direto) => {
    if (status === 'APPROVED' && direto) return <Badge className="bg-cyan-100 text-cyan-700"><Pin className="w-3 h-3 mr-1" />Selecionado pela empresa</Badge>;
    switch (status) {
      case 'APPROVED':
        return <Badge className="bg-green-100 text-green-700"><CheckCircle className="w-3 h-3 mr-1" />Aprovado</Badge>;
      case 'PENDING':
        return <Badge className="bg-amber-100 text-amber-700"><Clock className="w-3 h-3 mr-1" />Pendente</Badge>;
      case 'REJECTED':
        return <Badge className="bg-red-100 text-red-700"><XCircle className="w-3 h-3 mr-1" />Rejeitado</Badge>;
      default:
        return null;
    }
  };

  if (loading) {
    return (
      <DashboardLayout menuItems={companyMenuItems}>
        <div className="flex items-center justify-center py-20">
          <Loader2 className="w-8 h-8 animate-spin text-cyan-600" />
        </div>
      </DashboardLayout>
    );
  }

  if (!job) {
    return (
      <DashboardLayout menuItems={companyMenuItems}>
        <div className="text-center py-20">
          <p className="text-slate-600">Vaga não encontrada</p>
          <Button onClick={() => navigate('/company/jobs')} className="mt-4">
            Voltar
          </Button>
        </div>
      </DashboardLayout>
    );
  }

  const approvedCount = candidates.filter(c => c.status === 'APPROVED').length;
  const selectedCount = candidates.filter(c => c.is_selected).length;
  const pendingCount = candidates.filter(c => c.status === 'PENDING').length;

  return (
    <DashboardLayout menuItems={companyMenuItems}>
      <div className="space-y-6" data-testid="job-candidates">
        {/* Header */}
        <div className="flex items-start justify-between">
          <div className="flex items-start gap-4">
            <Button variant="ghost" size="icon" onClick={() => navigate('/company/jobs')}>
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <div>
              <h1 className="text-2xl font-bold text-slate-900">{job.titulo}</h1>
              <div className="flex items-center gap-3 mt-1">
                <span className="text-sm text-slate-600">{job.localizacao}</span>
                <Badge className={getStatusColor(job.status || 'em_andamento')}>
                  {getStatusLabel(job.status || 'em_andamento')}
                </Badge>
              </div>
            </div>
          </div>
          <div className="flex gap-2">
            <Button 
              variant="outline" 
              onClick={() => {
                setNewJobStatus(job.status || 'em_andamento');
                setShowStatusDialog(true);
              }}
            >
              Alterar Status
            </Button>
            <Button 
              variant="outline"
              onClick={handleExportPDF}
            >
              <Download className="w-4 h-4 mr-2" />
              Exportar PDF
            </Button>
          </div>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <Card className="border-slate-200">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                  <Users className="w-5 h-5 text-blue-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-slate-900">{candidates.length}</p>
                  <p className="text-xs text-slate-500">Total Convites</p>
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card className="border-slate-200">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-amber-100 rounded-lg flex items-center justify-center">
                  <Clock className="w-5 h-5 text-amber-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-slate-900">{pendingCount}</p>
                  <p className="text-xs text-slate-500">Pendentes</p>
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card className="border-slate-200">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center">
                  <CheckCircle className="w-5 h-5 text-green-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-slate-900">{approvedCount}</p>
                  <p className="text-xs text-slate-500">Aprovados</p>
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card className="border-slate-200">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-cyan-100 rounded-lg flex items-center justify-center">
                  <Pin className="w-5 h-5 text-cyan-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-slate-900">{selectedCount}</p>
                  <p className="text-xs text-slate-500">Selecionados</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Actions Bar */}
        <Card className="border-slate-200">
          <CardContent className="p-4">
            <div className="flex flex-wrap items-center gap-4">
              {/* Show Reopen button for finalized/cancelled jobs */}
              {(job?.status === 'finalizada' || job?.status === 'cancelada') ? (
                <Button 
                  onClick={handleReopenJob}
                  disabled={reopening}
                  className="bg-green-600 hover:bg-green-700"
                >
                  {reopening ? (
                    <Loader2 className="w-4 h-4 animate-spin mr-2" />
                  ) : (
                    <CheckCircle className="w-4 h-4 mr-2" />
                  )}
                  Reabrir Vaga
                </Button>
              ) : (
                <Button 
                  onClick={() => setShowSearchModal(true)}
                  className="bg-cyan-600 hover:bg-cyan-700"
                >
                  <Search className="w-4 h-4 mr-2" />
                  Buscar Talentos
                </Button>
              )}
              
              {/* Ranking Button - only if perfil_ideal is configured */}
              {job?.perfil_ideal?.niveis?.length > 0 && (
                <Button 
                  onClick={() => {
                    setShowRanking(true);
                    fetchRanking();
                  }}
                  className="bg-amber-500 hover:bg-amber-600 text-white"
                >
                  <Star className="w-4 h-4 mr-2" />
                  Ver Ranking
                </Button>
              )}
              
              {/* Comparison Report Button - always visible */}
              <Button 
                onClick={() => navigate(`/company/jobs/${jobId}/comparison`)}
                className="bg-cyan-600 hover:bg-cyan-700 text-white"
                data-testid="comparison-report-btn"
              >
                <BarChart3 className="w-4 h-4 mr-2" />
                Relatório Comparativo
              </Button>
              
              {/* Edit Profile Button - Redirects to Engenharia de Cargo */}
              <Button 
                variant="outline"
                onClick={() => {
                  if (job?.cargo_id) {
                    navigate(`/company/job-positions/${job.cargo_id}/edit`, { state: { focusSection: 'perfil' } });
                  } else {
                    navigate('/company/job-positions', { state: { showMessage: 'Crie ou vincule um cargo para definir o Perfil Ideal' } });
                  }
                }}
                className="border-purple-400 text-purple-700 hover:bg-purple-50"
              >
                <ClipboardList className="w-4 h-4 mr-2" />
                Editar Perfil Ideal
              </Button>
              
              <Separator orientation="vertical" className="h-8" />
              
              <div className="flex items-center gap-2">
                <Filter className="w-4 h-4 text-slate-400" />
                <span className="text-sm text-slate-600">Filtrar:</span>
              </div>
              
              <Select value={statusFilter} onValueChange={setStatusFilter}>
                <SelectTrigger className="w-36">
                  <SelectValue placeholder="Status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todos</SelectItem>
                  <SelectItem value="PENDING">Pendente</SelectItem>
                  <SelectItem value="APPROVED">Aprovado</SelectItem>
                  <SelectItem value="REJECTED">Rejeitado</SelectItem>
                </SelectContent>
              </Select>
              
              <Button 
                variant={showOnlySelected ? 'default' : 'outline'}
                size="sm"
                onClick={() => setShowOnlySelected(!showOnlySelected)}
                className={showOnlySelected ? 'bg-cyan-600 hover:bg-cyan-700' : ''}
              >
                <Pin className="w-4 h-4 mr-2" />
                Somente Selecionados
              </Button>
            </div>
          </CardContent>
        </Card>

        <JobInterviewsSection jobId={jobId} refreshKey={interviewsKey} onChange={() => setInterviewsKey((k) => k + 1)} />

        {/* Candidates List - Full Width (No side panel in list view) */}
        <div className="space-y-4">
          <h3 className="font-semibold text-slate-900">Candidatos ({candidates.length})</h3>
          
          {candidates.length === 0 ? (
            <Card className="border-slate-200">
              <CardContent className="py-12 text-center">
                <Users className="w-10 h-10 text-slate-300 mx-auto mb-3" />
                <p className="text-slate-600 mb-4">
                  {showOnlySelected 
                    ? 'Nenhum candidato selecionado ainda' 
                    : 'Nenhum candidato convidado para esta vaga'}
                </p>
                <Button onClick={() => setShowSearchModal(true)} className="bg-cyan-600 hover:bg-cyan-700">
                  <Search className="w-4 h-4 mr-2" />
                  Buscar Talentos
                </Button>
              </CardContent>
            </Card>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {candidates.map((candidate) => (
                <Card 
                  key={candidate.solicitation_id}
                  className={`border-slate-200 hover:shadow-md transition-all ${
                    candidate.is_selected ? 'ring-2 ring-cyan-500' : ''
                  }`}
                  data-testid={`candidate-${candidate.candidate_id}`}
                >
                  <CardContent className="p-4">
                    {/* Header */}
                    <div className="flex items-start gap-3 mb-3">
                      {candidate.candidate_foto_url ? (
                        <img 
                          src={`${process.env.REACT_APP_BACKEND_URL}${candidate.candidate_foto_url}`}
                          alt={candidate.candidate_name}
                          className="w-12 h-12 rounded-lg object-cover"
                        />
                      ) : (
                        <div className="w-12 h-12 bg-gradient-to-br from-slate-100 to-slate-200 rounded-lg flex items-center justify-center flex-shrink-0">
                          <User className="w-6 h-6 text-slate-400" />
                        </div>
                      )}
                      
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-1">
                          {candidate.is_selected && (
                            <Pin className="w-4 h-4 text-cyan-600 fill-cyan-600" />
                          )}
                          <h4 className="font-semibold text-slate-900 truncate">
                            {candidate.candidate_name || 'Candidato'}
                          </h4>
                        </div>
                        <p className="text-sm text-slate-600 truncate">{candidate.candidate_cargo}</p>
                        <div className="mt-1"><MatchBadge match={jobMatches[`${candidate.candidate_id}|${jobId}`]} testid={`job-match-${candidate.candidate_id}`} /></div>
                      </div>
                    </div>
                    
                    {/* Status Badge */}
                    <div className="flex flex-wrap items-center gap-2 mb-3">
                      {getCandidateStatusBadge(candidate.status, candidate.acesso_direto)}
                      
                      {candidate.candidate_disc && (
                        <Badge variant="outline" className="text-xs">
                          <Brain className="w-3 h-3 mr-1" />
                          {candidate.candidate_disc}
                        </Badge>
                      )}
                      
                      {candidate.has_evaluation && (
                        <Badge className="bg-purple-100 text-purple-700">
                          <Star className="w-3 h-3 mr-1 fill-purple-600" />
                          {candidate.evaluation_media || '-'}
                        </Badge>
                      )}
                    </div>
                    
                    {/* Location (if approved) */}
                    {candidate.candidate_localizacao && (
                      <p className="text-xs text-slate-500 mb-3 flex items-center gap-1">
                        <MapPin className="w-3 h-3" />
                        {candidate.candidate_localizacao}
                      </p>
                    )}
                    
                    {/* Actions */}
                    <div className="flex flex-wrap gap-2 pt-3 border-t">
                      {(
                        <>
                          <Button 
                            variant="outline" 
                            size="sm"
                            onClick={() => handleViewCandidate(candidate)}
                          >
                            <Eye className="w-3 h-3 mr-1" />
                            Perfil
                          </Button>
                          
                          <Button 
                            variant="outline" 
                            size="sm"
                            onClick={() => openChat(candidate)}
                          >
                            <MessageSquare className="w-3 h-3 mr-1" />
                            Chat
                          </Button>
                          
                          <Button 
                            variant="outline" 
                            size="sm"
                            onClick={() => initializeEvaluation(candidate)}
                          >
                            <ClipboardList className="w-3 h-3 mr-1" />
                            Avaliar
                          </Button>

                          <ScheduleInterviewButton jobId={jobId} candidateId={candidate.candidate_id} candidateName={candidate.candidate_name} refreshKey={interviewsKey} onSaved={() => setInterviewsKey((k) => k + 1)} />
                          
                          {candidate.is_selected ? (
                            <Button 
                              variant="ghost" 
                              size="sm"
                              onClick={() => handleUnselectCandidate(candidate.candidate_id)}
                              className="text-slate-500"
                            >
                              <PinOff className="w-3 h-3 mr-1" />
                              Desfixar
                            </Button>
                          ) : (
                            <Button 
                              size="sm"
                              onClick={() => handleSelectCandidate(candidate.candidate_id)}
                              className="bg-cyan-600 hover:bg-cyan-700"
                            >
                              <Pin className="w-3 h-3 mr-1" />
                              Fixar
                            </Button>
                          )}
                        </>
                      )}
                      
                      {candidate.status === 'PENDING' && (
                        <p className="text-xs text-amber-600 italic">
                          Convite enviado ao candidato (não é preciso esperar o aceite)
                        </p>
                      )}
                      
                      {candidate.status === 'REJECTED' && (
                        <p className="text-xs text-red-600 italic">
                          Candidato recusou o convite
                        </p>
                      )}
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </div>

        {/* Search Modal */}
        <Dialog open={showSearchModal} onOpenChange={setShowSearchModal}>
          <DialogContent className="max-w-3xl max-h-[80vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Search className="w-5 h-5 text-cyan-600" />
                Buscar Talentos para {job.titulo}
              </DialogTitle>
            </DialogHeader>
            
            <div className="space-y-4 mt-4">
              {/* Search Filters */}
              <div className="space-y-4">
                {/* Cargos - Multiple selection */}
                <div className="space-y-2">
                  <Label className="text-xs font-medium">Cargos (múltiplos)</Label>
                  <div className="flex gap-2">
                    <Input
                      placeholder="Digite um cargo e pressione Enter"
                      value={newCargoInput}
                      onChange={(e) => setNewCargoInput(e.target.value)}
                      onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), handleAddCargo())}
                      className="flex-1"
                    />
                    <Button type="button" variant="outline" onClick={handleAddCargo} size="sm">
                      <Plus className="w-4 h-4" />
                    </Button>
                  </div>
                  {searchFilters.cargos.length > 0 && (
                    <div className="flex flex-wrap gap-2 mt-2">
                      {searchFilters.cargos.map((cargo, i) => (
                        <Badge 
                          key={i} 
                          variant="secondary"
                          className="bg-cyan-50 text-cyan-700 cursor-pointer hover:bg-red-50 hover:text-red-700"
                          onClick={() => handleRemoveCargo(cargo)}
                        >
                          {cargo}
                          <X className="w-3 h-3 ml-1" />
                        </Badge>
                      ))}
                    </div>
                  )}
                  <p className="text-xs text-slate-500">
                    Adicione múltiplos cargos para ampliar a busca (Ex: Desenvolvedor, Programador, Analista)
                  </p>
                </div>
                
                <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                  <div className="space-y-1.5">
                    <Label className="text-xs">Localização</Label>
                    <Input
                      placeholder="Cidade ou Estado"
                      value={searchFilters.localizacao}
                      onChange={(e) => setSearchFilters({...searchFilters, localizacao: e.target.value})}
                    />
                  </div>
                  <div className="space-y-1.5">
                    <Label className="text-xs">Competências</Label>
                    <Input
                      placeholder="Ex: Python, Excel"
                      value={searchFilters.competencias}
                      onChange={(e) => setSearchFilters({...searchFilters, competencias: e.target.value})}
                    />
                  </div>
                  <div className="space-y-1.5">
                    <Label className="text-xs flex items-center gap-1">
                      <Brain className="w-3 h-3" /> Perfil DISC
                    </Label>
                    <Select
                      value={searchFilters.disc}
                      onValueChange={(v) => setSearchFilters({...searchFilters, disc: v})}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Qualquer" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">Qualquer</SelectItem>
                        <SelectItem value="D">D - Dominância</SelectItem>
                        <SelectItem value="I">I - Influência</SelectItem>
                        <SelectItem value="S">S - Estabilidade</SelectItem>
                        <SelectItem value="C">C - Conformidade</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-1.5">
                    <Label className="text-xs">PcD</Label>
                    <Select
                      value={searchFilters.pcd}
                      onValueChange={(v) => setSearchFilters({...searchFilters, pcd: v})}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Todos" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">Todos</SelectItem>
                        <SelectItem value="tambem">Incluir PcD</SelectItem>
                        <SelectItem value="somente">Somente PcD</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-1.5">
                    <Label className="text-xs">Estagiário</Label>
                    <Select
                      value={searchFilters.estagiario}
                      onValueChange={(v) => setSearchFilters({...searchFilters, estagiario: v})}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Todos" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">Todos</SelectItem>
                        <SelectItem value="tambem">Incluir</SelectItem>
                        <SelectItem value="somente">Somente</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-1.5">
                    <Label className="text-xs">Aprendiz</Label>
                    <Select
                      value={searchFilters.aprendiz}
                      onValueChange={(v) => setSearchFilters({...searchFilters, aprendiz: v})}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Todos" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">Todos</SelectItem>
                        <SelectItem value="tambem">Incluir</SelectItem>
                        <SelectItem value="somente">Somente</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              </div>
              
              <Button 
                onClick={handleSearch}
                disabled={searchLoading}
                className="w-full bg-cyan-600 hover:bg-cyan-700"
              >
                {searchLoading ? (
                  <Loader2 className="w-4 h-4 animate-spin mr-2" />
                ) : (
                  <Search className="w-4 h-4 mr-2" />
                )}
                Buscar
              </Button>
              
              {/* Info about privacy */}
              <div className="flex items-center gap-2 p-3 bg-cyan-50 rounded-lg text-sm text-cyan-700">
                <Lock className="w-4 h-4" />
                <span>Dados pessoais só serão revelados após aprovação do candidato</span>
              </div>
              
              {/* Search Results */}
              {searchResults.length > 0 && (
                <div className="space-y-3 pt-4 border-t">
                  <p className="text-sm text-slate-600">{searchResults.length} candidato(s) encontrado(s)</p>
                  
                  <div className="space-y-2 max-h-[300px] overflow-y-auto">
                    {searchResults.map((candidate) => (
                      <div 
                        key={candidate.id}
                        className="flex items-center justify-between p-3 bg-slate-50 rounded-lg"
                      >
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 bg-slate-200 rounded-lg flex items-center justify-center">
                            <User className="w-5 h-5 text-slate-500" />
                          </div>
                          <div>
                            <p className="font-medium text-slate-900">{candidate.cargo}</p>
                            <div className="flex items-center gap-2 text-xs text-slate-500">
                              {candidate.localizacao && (
                                <span className="flex items-center gap-1">
                                  <MapPin className="w-3 h-3" />
                                  {candidate.localizacao}
                                </span>
                              )}
                              {candidate.disc_profile && (
                                <Badge variant="outline" className="text-xs">
                                  {candidate.disc_profile}
                                </Badge>
                              )}
                              {candidate.is_pcd && (
                                <Badge className="bg-purple-100 text-purple-700 text-xs">PcD</Badge>
                              )}
                              {candidate.is_estagiario && (
                                <Badge className="bg-blue-100 text-blue-700 text-xs">Estagiário</Badge>
                              )}
                              {candidate.is_aprendiz && (
                                <Badge className="bg-green-100 text-green-700 text-xs">Aprendiz</Badge>
                              )}
                            </div>
                          </div>
                        </div>
                        
                        <Button
                          size="sm"
                          onClick={() => handleInviteCandidate(candidate.id)}
                          disabled={inviting === candidate.id}
                          className="bg-cyan-600 hover:bg-cyan-700"
                        >
                          {inviting === candidate.id ? (
                            <Loader2 className="w-4 h-4 animate-spin" />
                          ) : (
                            <>
                              <UserPlus className="w-4 h-4 mr-1" />
                              Convidar
                            </>
                          )}
                        </Button>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </DialogContent>
        </Dialog>

        {/* Candidate Detail Sheet */}
        <Sheet open={!!selectedCandidate && !showEvaluation && !showChatSheet && !loadingEvaluation} onOpenChange={() => setSelectedCandidate(null)}>
          <SheetContent className="w-full sm:max-w-lg overflow-y-auto">
            <SheetHeader>
              <SheetTitle>Perfil do Candidato</SheetTitle>
            </SheetHeader>
            
            {loadingDetail ? (
              <div className="flex justify-center py-12">
                <Loader2 className="w-6 h-6 animate-spin text-cyan-600" />
              </div>
            ) : candidateDetail ? (
              <div className="mt-6 space-y-6">
                {/* Photo and Name */}
                <div className="flex items-center gap-4">
                  {candidateDetail.foto_url ? (
                    <img 
                      src={`${process.env.REACT_APP_BACKEND_URL}${candidateDetail.foto_url}`}
                      alt={candidateDetail.nome}
                      className="w-20 h-20 rounded-xl object-cover"
                    />
                  ) : (
                    <div className="w-20 h-20 bg-gradient-to-br from-cyan-100 to-slate-100 rounded-xl flex items-center justify-center">
                      <User className="w-10 h-10 text-cyan-600" />
                    </div>
                  )}
                  <div>
                    <h3 className="text-lg font-semibold text-slate-900">{candidateDetail.nome}</h3>
                    <p className="text-slate-600">{candidateDetail.cargo}</p>
                  </div>
                </div>
                
                <Separator />
                
                {/* Contact Info */}
                <div className="grid grid-cols-2 gap-3">
                  {candidateDetail.email && (
                    <div className="flex items-center gap-2 text-sm text-slate-600">
                      <Mail className="w-4 h-4" />
                      <span className="truncate">{candidateDetail.email}</span>
                    </div>
                  )}
                  {candidateDetail.telefone && (
                    <div className="flex items-center gap-2 text-sm text-slate-600">
                      <Phone className="w-4 h-4" />
                      {candidateDetail.telefone}
                    </div>
                  )}
                  {candidateDetail.localizacao && (
                    <div className="flex items-center gap-2 text-sm text-slate-600">
                      <MapPin className="w-4 h-4" />
                      {candidateDetail.localizacao}
                    </div>
                  )}
                  {candidateDetail.disc_profile && (
                    <div className="flex items-center gap-2 text-sm text-slate-600">
                      <Brain className="w-4 h-4" />
                      DISC: {candidateDetail.disc_profile}
                    </div>
                  )}
                </div>
                
                {/* Big Five Section */}
                {candidateDetail.bigfive_result && candidateDetail.bigfive_result.scores && (
                  <>
                    <Separator />
                    <div>
                      <div className="flex items-center justify-between mb-3">
                        <Label className="text-xs text-slate-500 flex items-center gap-1">
                          <Lightbulb className="w-3 h-3" />
                          Big Five (OCEAN)
                        </Label>
                        <div className="flex items-center gap-2">
                          <Badge 
                            variant="outline" 
                            className={candidateDetail.bigfive_result.tipo === 'oficial' 
                              ? 'bg-blue-50 text-blue-700 border-blue-200' 
                              : 'bg-amber-50 text-amber-700 border-amber-200'
                            }
                          >
                            {candidateDetail.bigfive_result.tipo === 'oficial' ? 'Oficial' : 'Informal'}
                          </Badge>
                          <span className="text-xs text-slate-400">
                            {candidateDetail.bigfive_result.versao}
                          </span>
                        </div>
                      </div>
                      
                      {/* Mini Radar Chart */}
                      <div className="flex justify-center mb-3">
                        <BigFiveMiniRadar scores={candidateDetail.bigfive_result.scores} />
                      </div>
                      
                      {/* Trait Bars */}
                      <div className="space-y-2">
                        {[
                          { key: 'O', name: 'Abertura', color: 'bg-purple-500' },
                          { key: 'C', name: 'Conscienciosidade', color: 'bg-blue-500' },
                          { key: 'E', name: 'Extroversão', color: 'bg-amber-500' },
                          { key: 'A', name: 'Amabilidade', color: 'bg-rose-500' },
                          { key: 'N', name: 'Estabilidade', color: 'bg-emerald-500' }
                        ].map(({ key, name, color }) => {
                          const score = candidateDetail.bigfive_result.scores[key] || 50;
                          return (
                            <div key={key}>
                              <div className="flex justify-between text-xs mb-0.5">
                                <span className="text-slate-600">{name}</span>
                                <span className="font-medium">{score}%</span>
                              </div>
                              <div className="h-1.5 bg-slate-200 rounded-full overflow-hidden">
                                <div 
                                  className={`h-full ${color} rounded-full`}
                                  style={{ width: `${score}%` }}
                                />
                              </div>
                            </div>
                          );
                        })}
                      </div>
                      
                      {/* Validity info */}
                      {candidateDetail.bigfive_expires_at && (
                        <p className="text-xs text-slate-400 text-center mt-2">
                          Válido até: {new Date(candidateDetail.bigfive_expires_at).toLocaleDateString('pt-BR')}
                        </p>
                      )}
                      
                      {/* Request new test button */}
                      <Button 
                        variant="outline" 
                        size="sm" 
                        className="w-full mt-3 text-xs"
                        onClick={() => handleRequestNewBigFive(candidateDetail)}
                        disabled={requestingBigFive === candidateDetail.id}
                        data-testid="request-bigfive-btn"
                      >
                        {requestingBigFive === candidateDetail.id ? (
                          <Loader2 className="w-3 h-3 mr-1 animate-spin" />
                        ) : (
                          <RefreshCw className="w-3 h-3 mr-1" />
                        )}
                        Solicitar Nova Avaliação
                      </Button>
                    </div>
                  </>
                )}
                
                {/* No Big Five yet - show request button */}
                {(!candidateDetail.bigfive_result || !candidateDetail.bigfive_result.scores) && job?.bigfive_required && job.bigfive_required !== 'none' && (
                  <>
                    <Separator />
                    <div>
                      <Label className="text-xs text-slate-500 flex items-center gap-1">
                        <Lightbulb className="w-3 h-3" />
                        Big Five (OCEAN)
                      </Label>
                      <div className="text-center py-4 bg-slate-50 rounded-lg mt-2">
                        <Lightbulb className="w-8 h-8 text-slate-300 mx-auto mb-2" />
                        <p className="text-sm text-slate-500 mb-3">
                          Candidato ainda não realizou o Big Five
                        </p>
                        <Button 
                          variant="outline" 
                          size="sm"
                          onClick={() => handleRequestNewBigFive(candidateDetail)}
                          disabled={requestingBigFive === candidateDetail.id}
                          data-testid="request-bigfive-first-btn"
                        >
                          {requestingBigFive === candidateDetail.id ? (
                            <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                          ) : (
                            <Send className="w-4 h-4 mr-2" />
                          )}
                          Solicitar Avaliação ({job.bigfive_required})
                        </Button>
                      </div>
                    </div>
                  </>
                )}
                
                {/* Skills */}
                {candidateDetail.competencias?.length > 0 && (
                  <>
                    <Separator />
                    <div>
                      <Label className="text-xs text-slate-500">Competências</Label>
                      <div className="flex flex-wrap gap-1 mt-1">
                        {candidateDetail.competencias.map((comp, i) => (
                          <Badge key={i} variant="secondary" className="text-xs">
                            {comp}
                          </Badge>
                        ))}
                      </div>
                    </div>
                  </>
                )}
                
                {/* Resume */}
                {candidateDetail.resumo && (
                  <>
                    <Separator />
                    <div>
                      <Label className="text-xs text-slate-500">Resumo</Label>
                      <p className="text-sm text-slate-700 mt-1">{candidateDetail.resumo}</p>
                    </div>
                  </>
                )}
                
                {/* Curriculo */}
                {candidateDetail.curriculo_url && (
                  <Button variant="outline" className="w-full" data-testid="job-candidate-download-cv" onClick={async () => {
                    try {
                      const r = await candidatesAPI.curriculoFile(candidateDetail.id);
                      const a = document.createElement('a');
                      a.href = URL.createObjectURL(r.data);
                      a.download = candidateDetail.curriculo_nome || 'curriculo.pdf';
                      a.click();
                      URL.revokeObjectURL(a.href);
                    } catch { toast.error('Não foi possível baixar o currículo'); }
                  }}>
                    <FileText className="w-4 h-4 mr-2" />
                    Baixar Currículo
                  </Button>
                )}
              </div>
            ) : (
              <div className="text-center py-12">
                <Lock className="w-10 h-10 text-slate-300 mx-auto mb-3" />
                <p className="text-slate-600">Perfil não disponível</p>
              </div>
            )}
          </SheetContent>
        </Sheet>

        {/* Chat Sheet */}
        <Sheet open={showChatSheet} onOpenChange={setShowChatSheet}>
          <SheetContent className="w-full sm:max-w-lg flex flex-col">
            <SheetHeader>
              <SheetTitle className="flex items-center gap-2">
                <MessageSquare className="w-5 h-5 text-cyan-600" />
                Chat com {selectedCandidate?.candidate_name || 'Candidato'}
              </SheetTitle>
            </SheetHeader>
            
            <div className="flex-1 flex flex-col mt-4 min-h-0">
              {chatLoading ? (
                <div className="flex-1 flex items-center justify-center">
                  <Loader2 className="w-6 h-6 animate-spin text-cyan-600" />
                </div>
              ) : (
                <>
                  {/* Messages */}
                  <div className="flex-1 overflow-y-auto space-y-3 mb-4 p-4 bg-slate-50 rounded-lg">
                    {chatMessages.length === 0 ? (
                      <p className="text-center text-slate-500 py-8">
                        Nenhuma mensagem ainda. Inicie a conversa!
                      </p>
                    ) : (
                      chatMessages.map((msg, i) => (
                        <div 
                          key={i}
                          className={`flex ${msg.sender === 'COMPANY' ? 'justify-end' : 'justify-start'}`}
                        >
                          <div className={`max-w-[80%] p-3 rounded-lg ${
                            msg.sender === 'COMPANY' 
                              ? 'bg-cyan-600 text-white' 
                              : 'bg-white border'
                          }`}>
                            <p className="text-sm">{msg.content}</p>
                            <p className={`text-xs mt-1 ${
                              msg.sender === 'COMPANY' ? 'text-cyan-100' : 'text-slate-400'
                            }`}>
                              {new Date(msg.timestamp).toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })}
                            </p>
                          </div>
                        </div>
                      ))
                    )}
                  </div>
                  
                  {/* Input */}
                  <div className="flex gap-2">
                    <Input
                      placeholder="Digite sua mensagem..."
                      value={newMessage}
                      onChange={(e) => setNewMessage(e.target.value)}
                      onKeyPress={(e) => e.key === 'Enter' && sendMessage()}
                    />
                    <Button 
                      onClick={sendMessage}
                      disabled={sendingMessage || !newMessage.trim()}
                      className="bg-cyan-600 hover:bg-cyan-700"
                    >
                      {sendingMessage ? (
                        <Loader2 className="w-4 h-4 animate-spin" />
                      ) : (
                        <Send className="w-4 h-4" />
                      )}
                    </Button>
                  </div>
                </>
              )}
            </div>
          </SheetContent>
        </Sheet>

        {/* Status Update Dialog */}
        <AlertDialog open={showStatusDialog} onOpenChange={setShowStatusDialog}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Alterar Status da Vaga</AlertDialogTitle>
              <AlertDialogDescription>
                Selecione o novo status para a vaga "{job.titulo}"
              </AlertDialogDescription>
            </AlertDialogHeader>
            <div className="py-4">
              <Select value={newJobStatus} onValueChange={setNewJobStatus}>
                <SelectTrigger>
                  <SelectValue placeholder="Selecione o status" />
                </SelectTrigger>
                <SelectContent>
                  {JOB_STATUS_OPTIONS.map(option => (
                    <SelectItem key={option.value} value={option.value}>
                      {option.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <AlertDialogFooter>
              <AlertDialogCancel>Cancelar</AlertDialogCancel>
              <AlertDialogAction
                onClick={handleUpdateJobStatus}
                disabled={updatingStatus}
                className="bg-cyan-600 hover:bg-cyan-700"
              >
                {updatingStatus ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                Atualizar
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>

        {/* Evaluation Full Screen Dialog */}
        <Dialog open={showEvaluation} onOpenChange={(open) => {
          setShowEvaluation(open);
          if (!open) {
            setSelectedCandidate(null);
            setEvaluation(null);
          }
        }}>
          <DialogContent className="max-w-6xl h-[90vh] overflow-hidden flex flex-col">
            <DialogHeader className="flex-shrink-0">
              <DialogTitle className="flex items-center gap-3 text-xl">
                <div className="w-10 h-10 bg-cyan-100 rounded-lg flex items-center justify-center">
                  <ClipboardList className="w-5 h-5 text-cyan-600" />
                </div>
                Avaliação de Entrevista
              </DialogTitle>
            </DialogHeader>
            
            {selectedCandidate && evaluation && evaluation.niveis && (
              <div className="flex-1 overflow-y-auto mt-4">
                <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                  {/* Left Column - Candidate Info & Overall Score */}
                  <div className="space-y-4">
                    {/* Candidate Info */}
                    <Card className="border-slate-200">
                      <CardContent className="p-4">
                        <div className="flex items-center gap-4">
                          <div className="w-14 h-14 bg-gradient-to-br from-cyan-100 to-cyan-200 rounded-xl flex items-center justify-center">
                            <User className="w-7 h-7 text-cyan-600" />
                          </div>
                          <div>
                            <h3 className="font-semibold text-lg">{selectedCandidate.candidate_name || 'Candidato'}</h3>
                            <p className="text-sm text-slate-600">{selectedCandidate.candidate_cargo}</p>
                            {selectedCandidate.disc_profile && (
                              <Badge variant="outline" className="mt-1">
                                <Brain className="w-3 h-3 mr-1" />
                                DISC: {selectedCandidate.disc_profile}
                              </Badge>
                            )}
                          </div>
                        </div>
                      </CardContent>
                    </Card>

                    {/* Overall Score Display */}
                    <Card className="border-cyan-200 bg-gradient-to-br from-cyan-50 to-cyan-100">
                      <CardContent className="p-6">
                        <div className="text-center">
                          <span className="text-sm font-medium text-cyan-700 uppercase tracking-wide">Média Geral</span>
                          <div className="flex items-center justify-center gap-2 mt-2">
                            <Star className="w-8 h-8 fill-amber-400 text-amber-400" />
                            <span className="text-5xl font-bold text-cyan-900">{calculateOverallMedia()}</span>
                            <span className="text-xl text-cyan-600">/5</span>
                          </div>
                        </div>
                      </CardContent>
                    </Card>

                    {/* Quick Navigation - Níveis */}
                    <Card className="border-slate-200">
                      <CardHeader className="pb-2">
                        <CardTitle className="text-sm font-medium text-slate-600">Níveis de Avaliação</CardTitle>
                      </CardHeader>
                      <CardContent className="p-3 pt-0">
                        <div className="grid grid-cols-2 gap-2">
                          {evaluation.niveis.map((nivel) => {
                            const Icon = getNivelIcon(nivel.nivel);
                            const media = calculateNivelMedia(nivel);
                            return (
                              <div 
                                key={nivel.nivel}
                                className="flex items-center gap-2 p-2 bg-slate-50 rounded-lg hover:bg-slate-100 transition-colors"
                              >
                                <Icon className="w-4 h-4 text-cyan-600" />
                                <div className="flex-1 min-w-0">
                                  <p className="text-xs font-medium text-slate-700 truncate capitalize">{nivel.nivel}</p>
                                </div>
                                <Badge variant="outline" className="text-xs">{media}</Badge>
                              </div>
                            );
                          })}
                        </div>
                      </CardContent>
                    </Card>
                  </div>

                  {/* Right Column - Evaluation Tabs (2 cols wide) */}
                  <div className="lg:col-span-2">
                    <Card className="border-slate-200 h-full">
                      <CardContent className="p-4">
                        <Tabs defaultValue="entrega" className="w-full">
                          <TabsList className="w-full grid grid-cols-6 h-auto gap-1 p-1 bg-slate-100">
                            {evaluation.niveis.map((nivel) => {
                              const Icon = getNivelIcon(nivel.nivel);
                              return (
                                <TabsTrigger 
                                  key={nivel.nivel} 
                                  value={nivel.nivel}
                                  className="flex flex-col py-3 px-2 data-[state=active]:bg-white data-[state=active]:shadow-sm"
                                >
                                  <Icon className="w-5 h-5 mb-1" />
                                  <span className="text-xs text-center leading-tight capitalize">{nivel.nivel}</span>
                                  <span className="text-sm font-bold text-cyan-600">{calculateNivelMedia(nivel)}</span>
                                </TabsTrigger>
                              );
                            })}
                          </TabsList>
                          
                          {evaluation.niveis.map((nivel, nivelIndex) => (
                            <TabsContent key={nivel.nivel} value={nivel.nivel} className="mt-4 space-y-4">
                              <div className="p-4 bg-gradient-to-r from-slate-50 to-slate-100 rounded-lg border-l-4 border-cyan-500">
                                <h4 className="font-semibold text-slate-900 text-lg">{nivel.nome}</h4>
                                <p className="text-sm text-slate-600 mt-1">{nivel.descricao}</p>
                              </div>
                              
                              {/* Steps Grid */}
                              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                {nivel.steps.map((step, stepIndex) => (
                                  <div key={stepIndex} className="p-4 border rounded-lg space-y-3 bg-white hover:shadow-sm transition-shadow">
                                    <div className="flex items-start justify-between gap-2">
                                      <Label className="font-medium text-slate-800">{step.nome}</Label>
                                      <div className="flex gap-0.5 flex-shrink-0">
                                        {[1, 2, 3, 4, 5].map((star) => (
                                          <button
                                            key={star}
                                            type="button"
                                            onClick={() => {
                                              const newNiveis = [...evaluation.niveis];
                                              newNiveis[nivelIndex].steps[stepIndex].pontuacao = star;
                                              setEvaluation({...evaluation, niveis: newNiveis});
                                            }}
                                            className="p-0.5 hover:scale-110 transition-transform"
                                          >
                                            <Star className={`w-5 h-5 ${
                                              star <= (step.pontuacao || 0) 
                                                ? 'fill-amber-400 text-amber-400' 
                                                : 'text-slate-300'
                                            }`} />
                                          </button>
                                        ))}
                                      </div>
                                    </div>
                                    <Textarea
                                      placeholder="Observações..."
                                      value={step.observacao || ''}
                                      onChange={(e) => {
                                        const newNiveis = [...evaluation.niveis];
                                        newNiveis[nivelIndex].steps[stepIndex].observacao = e.target.value;
                                        setEvaluation({...evaluation, niveis: newNiveis});
                                      }}
                                      className="text-sm min-h-[60px] resize-none"
                                    />
                                  </div>
                                ))}
                              </div>
                            </TabsContent>
                          ))}
                        </Tabs>
                      </CardContent>
                    </Card>
                  </div>
                </div>

                {/* Bottom Section - Pontos Fortes/Melhoria + Recomendação */}
                <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mt-6">
                  {/* Pontos Fortes */}
                  <Card className="border-green-200">
                    <CardHeader className="pb-2">
                      <CardTitle className="text-sm font-medium text-green-700 flex items-center gap-2">
                        <CheckCircle className="w-4 h-4" />
                        Pontos Fortes
                      </CardTitle>
                    </CardHeader>
                    <CardContent className="p-4 pt-0">
                      <div className="flex gap-2 mb-2">
                        <Input
                          placeholder="Adicionar ponto forte..."
                          value={newPontoForte}
                          onChange={(e) => setNewPontoForte(e.target.value)}
                          onKeyPress={(e) => e.key === 'Enter' && addPontoForte()}
                          className="text-sm"
                        />
                        <Button size="sm" variant="outline" onClick={addPontoForte} className="text-green-600">
                          <Plus className="w-4 h-4" />
                        </Button>
                      </div>
                      <div className="flex flex-wrap gap-1.5 max-h-24 overflow-y-auto">
                        {evaluation.pontos_fortes?.map((ponto, i) => (
                          <Badge 
                            key={i} 
                            className="bg-green-100 text-green-700 hover:bg-red-100 hover:text-red-700 cursor-pointer transition-colors"
                            onClick={() => {
                              const newPontos = evaluation.pontos_fortes.filter((_, idx) => idx !== i);
                              setEvaluation({...evaluation, pontos_fortes: newPontos});
                            }}
                          >
                            {ponto}
                            <X className="w-3 h-3 ml-1" />
                          </Badge>
                        ))}
                      </div>
                    </CardContent>
                  </Card>

                  {/* Pontos de Melhoria */}
                  <Card className="border-amber-200">
                    <CardHeader className="pb-2">
                      <CardTitle className="text-sm font-medium text-amber-700 flex items-center gap-2">
                        <AlertCircle className="w-4 h-4" />
                        Pontos de Melhoria
                      </CardTitle>
                    </CardHeader>
                    <CardContent className="p-4 pt-0">
                      <div className="flex gap-2 mb-2">
                        <Input
                          placeholder="Adicionar ponto de melhoria..."
                          value={newPontoMelhoria}
                          onChange={(e) => setNewPontoMelhoria(e.target.value)}
                          onKeyPress={(e) => e.key === 'Enter' && addPontoMelhoria()}
                          className="text-sm"
                        />
                        <Button size="sm" variant="outline" onClick={addPontoMelhoria} className="text-amber-600">
                          <Plus className="w-4 h-4" />
                        </Button>
                      </div>
                      <div className="flex flex-wrap gap-1.5 max-h-24 overflow-y-auto">
                        {evaluation.pontos_melhoria?.map((ponto, i) => (
                          <Badge 
                            key={i} 
                            className="bg-amber-100 text-amber-700 hover:bg-red-100 hover:text-red-700 cursor-pointer transition-colors"
                            onClick={() => {
                              const newPontos = evaluation.pontos_melhoria.filter((_, idx) => idx !== i);
                              setEvaluation({...evaluation, pontos_melhoria: newPontos});
                            }}
                          >
                            {ponto}
                            <X className="w-3 h-3 ml-1" />
                          </Badge>
                        ))}
                      </div>
                    </CardContent>
                  </Card>

                  {/* Recomendação Final */}
                  <Card className="border-slate-200">
                    <CardHeader className="pb-2">
                      <CardTitle className="text-sm font-medium text-slate-700">Recomendação Final</CardTitle>
                    </CardHeader>
                    <CardContent className="p-4 pt-0">
                      <div className="grid grid-cols-3 gap-2">
                        {[
                          { value: 'contratar', label: 'Contratar', color: 'bg-green-100 text-green-700 border-green-300 hover:bg-green-200' },
                          { value: 'revisar', label: 'Revisar', color: 'bg-amber-100 text-amber-700 border-amber-300 hover:bg-amber-200' },
                          { value: 'nao_contratar', label: 'Não Contratar', color: 'bg-red-100 text-red-700 border-red-300 hover:bg-red-200' }
                        ].map(opt => (
                          <Button
                            key={opt.value}
                            variant="outline"
                            className={`h-auto py-3 ${evaluation.recomendacao === opt.value ? opt.color : ''}`}
                            onClick={() => setEvaluation({...evaluation, recomendacao: opt.value})}
                          >
                            {opt.label}
                          </Button>
                        ))}
                      </div>
                    </CardContent>
                  </Card>
                </div>

                {/* Action Buttons */}
                <div className="flex gap-4 pt-6 border-t mt-6">
                  <Button 
                    variant="outline" 
                    className="flex-1 h-12"
                    onClick={() => handleSaveEvaluation('rascunho')}
                    disabled={savingEvaluation}
                  >
                    {savingEvaluation ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Save className="w-4 h-4 mr-2" />}
                    Salvar Rascunho
                  </Button>
                  <Button 
                    className="flex-1 h-12 bg-cyan-600 hover:bg-cyan-700"
                    onClick={() => handleSaveEvaluation('finalizado')}
                    disabled={savingEvaluation}
                  >
                    {savingEvaluation ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <CheckCircle className="w-4 h-4 mr-2" />}
                    Finalizar Avaliação
                  </Button>
                </div>
              </div>
            )}
          </DialogContent>
        </Dialog>

        {/* Ranking Dialog */}
        <Dialog open={showRanking} onOpenChange={setShowRanking}>
          <DialogContent className="max-w-4xl max-h-[80vh] overflow-hidden flex flex-col">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2 text-xl">
                <Star className="w-6 h-6 text-amber-500" />
                Ranking de Candidatos
              </DialogTitle>
            </DialogHeader>
            
            <div className="flex-1 overflow-y-auto mt-4">
              {loadingRanking ? (
                <div className="flex items-center justify-center py-12">
                  <Loader2 className="w-8 h-8 animate-spin text-cyan-600" />
                </div>
              ) : ranking.length === 0 ? (
                <div className="text-center py-12">
                  <Users className="w-12 h-12 text-slate-300 mx-auto mb-4" />
                  <p className="text-slate-600">Nenhum candidato avaliado ainda</p>
                  <p className="text-sm text-slate-500 mt-2">
                    Avalie os candidatos para gerar o ranking de aderência
                  </p>
                </div>
              ) : (
                <div className="space-y-3">
                  {ranking.map((item, index) => (
                    <Card key={item.candidate_id} className={`border-slate-200 ${index === 0 ? 'ring-2 ring-amber-400' : ''}`}>
                      <CardContent className="p-4">
                        <div className="flex items-center gap-4">
                          {/* Position */}
                          <div className={`w-10 h-10 rounded-full flex items-center justify-center font-bold text-lg ${
                            index === 0 ? 'bg-amber-100 text-amber-700' :
                            index === 1 ? 'bg-slate-200 text-slate-700' :
                            index === 2 ? 'bg-orange-100 text-orange-700' :
                            'bg-slate-100 text-slate-600'
                          }`}>
                            {index + 1}º
                          </div>
                          
                          {/* Candidate Info */}
                          <div className="flex-1">
                            <div className="flex items-center gap-2">
                              <h4 className="font-semibold text-slate-900">{item.candidate_name}</h4>
                              {item.disc_profile && (
                                <Badge variant="outline" className={item.disc_match ? 'border-green-500 text-green-700' : ''}>
                                  <Brain className="w-3 h-3 mr-1" />
                                  {item.disc_profile}
                                  {item.disc_match && <CheckCircle className="w-3 h-3 ml-1" />}
                                </Badge>
                              )}
                            </div>
                            <p className="text-sm text-slate-600">{item.candidate_cargo}</p>
                          </div>
                          
                          {/* Adherence Score */}
                          <div className="text-right">
                            <div className="flex items-center gap-2">
                              <Star className={`w-5 h-5 ${item.aderencia >= 70 ? 'fill-amber-400 text-amber-400' : 'text-slate-300'}`} />
                              <span className={`text-2xl font-bold ${
                                item.aderencia >= 80 ? 'text-green-600' :
                                item.aderencia >= 60 ? 'text-amber-600' :
                                'text-slate-600'
                              }`}>
                                {item.aderencia}%
                              </span>
                            </div>
                            <p className="text-xs text-slate-500">Aderência</p>
                          </div>
                        </div>
                        
                        {/* Level scores */}
                        {Object.keys(item.nivel_scores || {}).length > 0 && (
                          <div className="mt-3 pt-3 border-t flex flex-wrap gap-2">
                            {Object.entries(item.nivel_scores).map(([nivel, score]) => (
                              <Badge key={nivel} variant="secondary" className="text-xs">
                                {nivel}: {score}%
                              </Badge>
                            ))}
                          </div>
                        )}
                        
                        {!item.has_evaluation && (
                          <p className="text-xs text-amber-600 mt-2">
                            * Candidato ainda não foi avaliado
                          </p>
                        )}
                      </CardContent>
                    </Card>
                  ))}
                </div>
              )}
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </DashboardLayout>
  );
}
